import { HttpParams } from '@angular/common/http';
import { forwardRef, Inject, Injectable } from '@angular/core';
import { Properties } from '../../common/properties';
import { Algorithm, AlgorithmOutput, Thing, WorkSession, WorkSessionDefinition } from '../../model';
import { AlgorithmService } from '../../service/algorithm.service';
import { AuthenticationService } from '../../service/authentication.service';
import { ContextService } from '../../service/context.service';
import { CustomPropertyService, CustomPropertyType } from '../../service/custom-property.service';
import { AbstractContextService } from '../../shared/class/abstract-context-service.class';
import { AbstractThingContextService } from '../../shared/class/abstract-thing-context-service.class';
import { CompositePartComponent, MetricDetailComponent, PropertyComponent } from '../../shared/component';
import { CustomTableColumn, CustomTableService, PipedTableColumn } from '../../shared/custom-table';
import { AbstractListWidgetV2Service } from '../../shared/list-widget-v2/abstract-list-widget-v2.service';
import { ClickOnRowBehaviour, DetailsModeType } from '../../shared/list-widget-v2/list-widget-v2.components';
import { ThingListWidgetV2Service } from '../thing-list/thing-list-widget-v2.service';

@Injectable()
export abstract class AbstractWorkSessionListWidgetV2Service<T> extends AbstractListWidgetV2Service<T> {

    constructor(
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => CustomPropertyService)) customPropertyService: CustomPropertyService,
        @Inject(forwardRef(() => AbstractContextService)) protected contextService: AbstractContextService,
        @Inject(forwardRef(() => AbstractThingContextService)) protected thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => ThingListWidgetV2Service)) private thingListWidgetV2Service: ThingListWidgetV2Service,
        @Inject(forwardRef(() => ContextService)) protected timezoneContextService: ContextService,
        @Inject(forwardRef(() => AlgorithmService)) private algorithmService: AlgorithmService
    ) {
        super(authenticationService, customPropertyService);
    }

    abstract downloadCSV(params: HttpParams, fileName: string): void;

    getColumnDescriptions(columns: (MetricDetailComponent | CompositePartComponent | PropertyComponent)[]): string[] {
        if (!columns) {
            return [];
        }
        return columns.map(c => {
            if (c instanceof PropertyComponent) {
                return this.getCustomPropertyDescription(c);
            } else {
                return c.description;
            }
        });
    }

    protected isColumnVisible(columnName: string): boolean {
        return true;
    }

    protected getLabel(col: MetricDetailComponent | CompositePartComponent | PropertyComponent, defaultType: string): string {
        if (col.label) {
            return col.label;
        }
        if (col.name.indexOf('properties.') > -1) {
            const propNameType = this.getCustomPropertyNameAndType(col.name);
            return this.customPropertyService.getLabelByTypeAndName(propNameType.type, propNameType.name) || propNameType.name;
        } else if (col.name == 'thing.parentThingId') {
            return 'parentThingProperty';
        } else {
            return Properties.getLabelByName(col.name, defaultType) || col.name;
        }
    }

    protected getCustomPropertyNameAndType(columnName: string): { name: string, type: CustomPropertyType } {
        let name = columnName;
        let type = CustomPropertyType.WorkSession;
        if (columnName.startsWith('customer.')) {
            type = CustomPropertyType.Customer;
            name = name.substr(9);
        } else if (columnName.startsWith('location.')) {
            type = CustomPropertyType.Location;
            name = name.substr(9);
        } else if (columnName.startsWith('thingDefinition.')) {
            type = CustomPropertyType.ThingDefinition;
            name = name.substr(16);
        } else if (columnName.startsWith('thing.')) {    //Thing
            type = CustomPropertyType.Thing;
            name = name.substr(6);
        }
        name = name.substr(11);
        return { name, type };
    }

    getParams(searchFields: string[], advancedSearchBody: any, forExport: boolean, includeSubThingsWorkSessions: boolean): HttpParams {
        let params = new HttpParams();
        if (this.thingContextService.getCurrentThing()) {
            params = params.set('thingId', this.thingContextService.getCurrentThing().id);
        } else if (this.contextService.getCurrentLocation()) {
            params = params.set('locationId', this.contextService.getCurrentLocation().id);
        } else if (this.contextService.getCurrentCustomer()) {
            params = params.set('customerId', this.contextService.getCurrentCustomer().id);
        } else if (this.contextService.getCurrentPartner()) {
            params = params.set('partnerId', this.contextService.getCurrentPartner().id);
        }
        if (advancedSearchBody) {
            if (advancedSearchBody.key) {
                params = params.set('searchText', "*" + advancedSearchBody.key + "*");
                searchFields.forEach(field => params = params.append('searchField', field));
            }
            if (advancedSearchBody.customer) {
                params = params.set('customerId', advancedSearchBody.customer);
            }
            if (advancedSearchBody.thingDefinitions && advancedSearchBody.thingDefinitions.length) {
                advancedSearchBody.thingDefinitions.forEach(id => params = params.append('thingDefinitionId', id));
            }
            if (advancedSearchBody.workSessionDefinitions && advancedSearchBody.workSessionDefinitions.length) {
                advancedSearchBody.workSessionDefinitions.forEach(id => params = params.append('workSessionDefinitionId', id));
            }
            if (advancedSearchBody.startTimestamp) {
                params = params.set('minStartTimestamp', advancedSearchBody.startTimestamp);
            }
            if (advancedSearchBody.endTimestamp) {
                params = params.set('maxStartTimestamp', advancedSearchBody.endTimestamp);
            }
            if (advancedSearchBody['thingDefinition.name']) {
                params = params.set('thingDefinition.name', advancedSearchBody['thingDefinition.name']);
            }
            if (advancedSearchBody['name']) {
                params = params.set('name', advancedSearchBody['name']);
            }
            if (advancedSearchBody['group']) {
                params = params.set('group', advancedSearchBody['group']);
            }
            if (advancedSearchBody['duration']) {
                params = params.set('duration', advancedSearchBody['duration']);
            }
            if (advancedSearchBody['topics']?.length) {
                params = params.set('topics', ('in;' + advancedSearchBody['topics'].join(',')));
            }
            if (advancedSearchBody.selectedThingIds && advancedSearchBody.selectedThingIds.length) {
                advancedSearchBody.selectedThingIds.forEach(id => params = params.append('selectedThingId', id));
            }
            const properties = Object.keys(advancedSearchBody).filter(key => key.includes('properties.'));
            properties.forEach(property => params = params.set(property, advancedSearchBody[property]));
            const postComputationResults = Object.keys(advancedSearchBody).filter(key => key.startsWith('postComputationResults.'));
            postComputationResults.forEach(result => params = params.set(result, advancedSearchBody[result]));
            const monitoredMetrics = Object.keys(advancedSearchBody).filter(key => key.startsWith('metrics.'));
            monitoredMetrics.forEach(result => params = params.set(result, advancedSearchBody[result]));
        }
        if (this.columnFieldNames && forExport) {
            this.columnFieldNames.map(name => {
                if (name == 'date') {
                    return 'startTimestamp';
                }
                return name;
            }).forEach(columnFieldName => params = params.append('selectField', columnFieldName));
        }
        if (this.columnFieldLabels && forExport) {
            params = params.append('labels', this.columnFieldLabels.join(';'));
        }
        if (forExport) {
            params = params.set('language', this.authenticationService.getUser().language || navigator.language);
            const localeTimezone = this.timezoneContextService.getTimezone();
            if (localeTimezone) {
                params = params.append('clientTimezone', localeTimezone);
            }
        }
        if (includeSubThingsWorkSessions) {
            params = params.set('includeSubThingsWorkSessions', 'true');
        }
        return params;
    }

    updatePageListContent(workSessions: WorkSession[], workSessionDefinitions: WorkSessionDefinition[], displayedColumns: CustomTableColumn[], isHistorical: boolean, detailsMode: DetailsModeType, clickOnRowBehaviour: ClickOnRowBehaviour): void {
        let thingList: Thing[] = [];
        workSessions.forEach(ws => {
            if (ws.thing) {
                thingList.push(ws.thing);
            }
        });
        this.thingListWidgetV2Service.addTags(thingList, this.contextService.getTagObjects());
        workSessions.forEach(ws => {
            const workSessionDefinition = workSessionDefinitions.find(wsd => wsd.id == ws.workSessionDefinitionId);
            ws['showDetailsButton'] = workSessionDefinition?.templateName && ws.thing != null;
            ws['durationRaw'] = ws.startTimestamp == null ? null : (ws.endTimestamp ?? Date.now()) - ws.startTimestamp;
            ws['showDownloadData'] = !!ws.dataImport?.storageKey;
        });
        if (clickOnRowBehaviour == ClickOnRowBehaviour.OPEN_CONTEXT_OBJECT && detailsMode != DetailsModeType.NONE && !displayedColumns.some(col => col.name == 'showDetails') && workSessions?.length && workSessions.some(a => a['showDetailsButton'])) {
            displayedColumns.push(CustomTableService.newButtonColumn('showDetails', '', 'id', 'float-right', 'detailsProperty').withMatIcon('info').withVisiblePath('showDetailsButton').withColumnClass('no-print'))
        }
    }

    preparePostComputationOutputMetadata(displayedColumns: CustomTableColumn[], workSessionDefinitions: WorkSessionDefinition[]): Promise<void> {
        const postComputationColumns = (displayedColumns || [])
            .filter(col => col instanceof PipedTableColumn && this.getPostComputationResultParts(col.path)) as PipedTableColumn[];
        if (!postComputationColumns.length || !workSessionDefinitions?.length) {
            return Promise.resolve();
        }
        const outputMap: { [workSessionDefinitionId: string]: { [propertyPath: string]: AlgorithmOutput } } = {};
        const algorithmCache: { [algorithmId: string]: Promise<Algorithm | null> } = {};
        const promises: Promise<void>[] = [];
        workSessionDefinitions.forEach(wsd => {
            postComputationColumns.forEach(column => {
                const parts = this.getPostComputationResultParts(column.path);
                if (!parts) {
                    return;
                }
                const binding = wsd.postComputationAlgorithms?.find(el => el.resultField == parts.resultField);
                if (binding?.algorithmId) {
                    if (!algorithmCache[binding.algorithmId]) {
                        algorithmCache[binding.algorithmId] = this.algorithmService.getAlgorithmById(binding.algorithmId).catch(() => null);
                    }
                    promises.push(algorithmCache[binding.algorithmId].then(algorithm => {
                        const output = algorithm?.outputs?.find(o => o.name == parts.outputField);
                        if (output) {
                            outputMap[wsd.id] = outputMap[wsd.id] || {};
                            outputMap[wsd.id][column.path] = output;
                        }
                    }));
                }
            });
        });
        return Promise.all(promises).then(() => {
            postComputationColumns.forEach(column => {
                column.withArgumentResolver((pipeArgument, element) => {
                    const output = outputMap[element?.workSessionDefinitionId]?.[column.path];
                    return output ? Object.assign({}, pipeArgument, { property: output }) : pipeArgument;
                });
            });
        });
    }

    private getPostComputationResultParts(path: string): { resultField: string, outputField: string } | null {
        if (!path?.startsWith('postComputationResults.')) {
            return null;
        }
        const parts = path.split('.');
        if (parts.length != 3) {
            return null;
        }
        return { resultField: parts[1], outputField: parts[2] };
    }
}